import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trophy, Users, Shield, ArrowRight, Sparkles, CheckCircle, Zap, Smartphone, Calendar, Award, Crown, Play } from 'lucide-react';
import { Button } from '../../components/ui';

export const RacketLandingPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "ProRacket | Dé #1 Padel & Tennis King of the Court & Ladder Competitie Planner"
        const metaDesc = document.querySelector('meta[name="description"]')
        if (metaDesc) {
            metaDesc.setAttribute("content", "Plan professionele padel-, tennis- en squash toernooien. King of the Court generator, dynamische laddercompetities en realtime courtflow timers.")
        }
    }, [])

    return (
        <div className="min-h-screen bg-[#050505] text-white selection:bg-green-500 selection:text-white relative overflow-x-hidden font-sans">
            {/* Background Neon Glows */}
            <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-gradient-to-br from-green-500/20 to-transparent rounded-full blur-[120px] pointer-events-none"></div>
            <div className="absolute bottom-0 right-[-10%] w-[60%] h-[60%] bg-gradient-to-br from-emerald-900/20 to-transparent rounded-full blur-[120px] pointer-events-none"></div>

            {/* Cyber Grid Overlay */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff02_1px,transparent_1px),linear-gradient(to_bottom,#ffffff02_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>

            {/* 🧭 NAVIGATION HEADER */}
            <nav className="fixed w-full z-50 top-0 transition-all duration-300 bg-black/60 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-3 group cursor-pointer" onClick={() => navigate('/')}>
                        <Award className="text-green-500 h-6 w-6 group-hover:scale-110 transition-transform" />
                        <span className="font-black text-xl tracking-tighter uppercase italic select-none flex gap-1">
                            <span className="text-white">PROPOULE</span>
                            <span className="text-green-500 font-light">RACKET</span>
                        </span>
                    </div>

                    {/* Central Brand Navigator Pill */}
                    <div className="hidden md:flex items-center gap-6 bg-white/5 border border-white/10 rounded-2xl px-6 py-2 backdrop-blur-lg">
                        <button onClick={() => navigate('/')} className="text-xs font-bold text-slate-300 hover:text-orange-400 transition-colors uppercase tracking-widest">⚽ ProPoule</button>
                        <span className="text-white/20">|</span>
                        <button onClick={() => navigate('/wkpoule')} className="text-xs font-bold text-slate-300 hover:text-orange-400 transition-colors uppercase tracking-widest">🏆 WK Poule</button>
                        <span className="text-white/20">|</span>
                        <button onClick={() => navigate('/e-sports')} className="text-xs font-bold text-slate-300 hover:text-purple-400 transition-colors uppercase tracking-widest">🎮 ProPoule Esports</button>
                        <span className="text-white/20">|</span>
                        <button onClick={() => navigate('/proracket')} className="text-xs font-bold text-green-400 uppercase tracking-widest">🎾 ProPoule Racket</button>
                    </div>

                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={() => navigate('/login')} className="hidden md:flex text-gray-300 hover:text-white">
                            Inloggen
                        </Button>
                        <Button 
                            onClick={() => navigate('/login')}
                            className="bg-green-500 hover:bg-green-600 text-black font-bold rounded-xl border-0 shadow-lg shadow-green-500/20 px-6"
                        >
                            Start Direct 🎾
                        </Button>
                    </div>
                </div>
            </nav>

            {/* 🚀 HERO SECTION */}
            <section className="relative pt-32 md:pt-44 pb-20 px-6 flex items-center">
                <div className="max-w-7xl mx-auto w-full">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className="text-left">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 mb-6 text-xs font-black uppercase tracking-widest">
                                <Sparkles size={14} />
                                <span>Premium Padel & Tennis Club Software</span>
                            </div>
                            
                            <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 leading-[1.05]">
                                De slimste software <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 drop-shadow-xl">
                                    voor padel & tennis
                                </span>
                            </h1>
                            
                            <p className="text-lg md:text-xl text-gray-400 mb-10 max-w-xl leading-relaxed">
                                Plan automatische <strong>King of the Court</strong> poules, beheer dynamische clubladders en stuur live scoreborden aan. Speciaal ontworpen voor racketsporten.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <Button
                                    onClick={() => navigate('/login')}
                                    className="bg-green-500 hover:bg-green-600 text-black font-extrabold text-base px-8 py-6 rounded-xl flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(34,197,94,0.3)] transition-all transform hover:-translate-y-0.5"
                                >
                                    <span>Start Gratis Toernooi</span>
                                    <ArrowRight size={18} />
                                </Button>
                                <Button
                                    onClick={() => navigate('/login')}
                                    className="bg-white/5 hover:bg-white/10 text-white border border-white/10 font-bold px-8 py-6 rounded-xl transition-all"
                                >
                                    Bekijk Demo-poule 🎾
                                </Button>
                            </div>
                        </div>

                        <div className="relative flex justify-center items-center">
                            <div className="absolute w-[350px] h-[350px] bg-green-500/10 rounded-full blur-[100px] pointer-events-none"></div>
                            <img
                                src="/images/padel_hero.png"
                                alt="ProPoule Racket Boardroom Padel League Dashboard Mockup"
                                className="relative rounded-3xl border border-white/10 shadow-[0_0_50px_rgba(34,197,94,0.15)] max-w-full hover:scale-[1.02] transition-transform duration-500"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* 📊 KEY FEATURES GRID */}
            <section className="py-24 border-t border-white/5 bg-black/40">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <span className="text-xs font-mono uppercase tracking-widest text-green-400 bg-green-500/10 border border-green-500/20 px-3 py-1.5 rounded-full mb-4 inline-block">Features</span>
                        <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">
                            Alles wat je nodig hebt voor een vlekkeloze toernooidag
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {/* feature 1 */}
                        <div className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:border-green-500/30 transition-all group">
                            <div className="w-12 h-12 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400 mb-6 group-hover:scale-110 transition-transform">
                                <Crown size={24} />
                            </div>
                            <h3 className="text-xl font-bold mb-3 text-white">King of the Court</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Automatische baanwissels, tijdschema's en doorlopende speelrondes. Het platform berekent direct wie stijgt of daalt op basis van behaalde punten.
                            </p>
                        </div>

                        {/* feature 2 */}
                        <div className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:border-green-500/30 transition-all group">
                            <div className="w-12 h-12 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400 mb-6 group-hover:scale-110 transition-transform">
                                <Zap size={24} />
                            </div>
                            <h3 className="text-xl font-bold mb-3 text-white">Dynamische Ladders</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Doorlopende clubcompetities met geautomatiseerde ranglijsten. Spelers dagen elkaar uit en de scores worden realtime verwerkt.
                            </p>
                        </div>

                        {/* feature 3 */}
                        <div className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:border-green-500/30 transition-all group">
                            <div className="w-12 h-12 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400 mb-6 group-hover:scale-110 transition-transform">
                                <Smartphone size={24} />
                            </div>
                            <h3 className="text-xl font-bold mb-3 text-white">Courtflow timers</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Koppel smart TVs en baantimers direct aan je speelschema. Inclusief automatische audio wissel-signalen en timers op de banen.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 🏁 CTA & FOOTER SECTION */}
            <section className="py-28 relative overflow-hidden bg-gradient-to-t from-green-950/20 to-transparent">
                <div className="max-w-4xl mx-auto text-center px-6 relative z-10">
                    <h2 className="text-4xl md:text-6xl font-black mb-6 text-white tracking-tight">
                        Klaar voor de eerste service?
                    </h2>
                    <p className="text-gray-300 text-lg mb-10 max-w-xl mx-auto">
                        Meld je gratis aan, maak een poule of ladder aan binnen 2 minuten, en laat het toernooi live van start gaan.
                    </p>
                    <Button
                        onClick={() => navigate('/login')}
                        className="bg-green-500 hover:bg-green-600 text-black font-extrabold text-lg px-10 py-6 rounded-xl shadow-[0_0_35px_rgba(34,197,94,0.4)]"
                    >
                        Nu Toernooi Genereren 🎾
                    </Button>
                </div>
            </section>

            {/* SEO Footnote */}
            <footer className="py-12 border-t border-white/5 text-center text-xs text-gray-500">
                <p>© {new Date().getFullYear()} ProRacket. Plan padel, tennis en squash toernooien.</p>
            </footer>
        </div>
    );
};
